import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { PlatformAdmin } from '../types';
import { adminApi } from '../services/adminApi';

interface AdminAuthContextType {
  admin: PlatformAdmin | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<PlatformAdmin>;
  logout: () => void;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [admin, setAdmin] = useState<PlatformAdmin | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!adminApi.hasToken()) {
      setIsLoading(false);
      return;
    }
    adminApi.getMe()
      .then(({ admin }) => setAdmin(admin))
      .catch(() => adminApi.clearToken())
      .finally(() => setIsLoading(false));
  }, []);

  const login = async (email: string, password: string) => {
    const { admin } = await adminApi.login(email, password);
    setAdmin(admin);
    return admin;
  };

  const logout = () => {
    adminApi.clearToken();
    setAdmin(null);
  };

  return (
    <AdminAuthContext.Provider
      value={{
        admin,
        isAuthenticated: !!admin,
        isLoading,
        login,
        logout,
      }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error('useAdminAuth must be used within AdminAuthProvider');
  return ctx;
}
